import { useMemo, useState } from "react";
import { X, Save, Search, DollarSign, UserPlus } from "lucide-react";
import { fmtBRL, COLOR_MAP } from "../lib/constants";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import PriceLookupModal from "../components/PriceLookupModal";

const BRANDS = ["Apple", "Samsung", "Motorola", "Xiaomi", "Outros"];
const MODELS = {
  Apple: [
    "iPhone X", "iPhone XR", "iPhone XS", "iPhone XS Max",
    "iPhone 11", "iPhone 11 Pro", "iPhone 11 Pro Max",
    "iPhone 12 mini", "iPhone 12", "iPhone 12 Pro", "iPhone 12 Pro Max",
    "iPhone 13 mini", "iPhone 13", "iPhone 13 Pro", "iPhone 13 Pro Max",
    "iPhone 14", "iPhone 14 Plus", "iPhone 14 Pro", "iPhone 14 Pro Max",
    "iPhone 15", "iPhone 15 Plus", "iPhone 15 Pro", "iPhone 15 Pro Max",
    "iPhone 16", "iPhone 16 Plus", "iPhone 16 Pro", "iPhone 16 Pro Max",
    "iPhone 17", "iPhone 17 Plus", "iPhone 17 Pro", "iPhone 17 Pro Max",
  ],
  Samsung: ["Galaxy S22", "Galaxy S23", "Galaxy S24", "Galaxy A54", "Galaxy A34"],
  Motorola: ["Moto G54", "Moto G84", "Edge 40"],
  Xiaomi: ["Redmi Note 12", "Redmi Note 13", "POCO X6"],
  Outros: [],
};

// Order matters — visual list
const SERVICE_LIST = [
  "Troca de tela",
  "Troca de bateria",
  "Troca de vidro",
  "Tampa",
  "Laser",
  "Manutenção placa",
  "Subir bateria",
  "Câmera traseira",
  "Câmera frontal",
  "Conector de carga",
  "Auto-falante",
];
const COLORS = Object.keys(COLOR_MAP);

const parseCurrency = (str) => {
  if (typeof str === "number") return str;
  return parseFloat(String(str || "").replace(/\./g, "").replace(",", ".")) || 0;
};

export default function NewOrderModal({ clients, nextNumber, onClose, onSave }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [showPriceLookup, setShowPriceLookup] = useState(false);
  const [form, setForm] = useState({
    orderNumber: nextNumber,
    clientId: "",
    clientName: "",
    clientPhone: "",
    brand: "Apple",
    model: "",
    color: "",
    imei: "",
    services: [],
    problem: "",
    observation: "",
    total: "",
    costValue: "",
    batteryPercent: "",
    batteryMsg: "",
    telaMsg: "",
  });
  const [clientQuery, setClientQuery] = useState("");
  const [customService, setCustomService] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState("");

  const filteredClients = useMemo(() => {
    const q = clientQuery.toLowerCase();
    if (!q) return [];
    return clients
      .filter((c) => c.name.toLowerCase().includes(q) || c.phone.includes(clientQuery))
      .slice(0, 5);
  }, [clientQuery, clients]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleService = (s) =>
    setField(
      "services",
      form.services.includes(s) ? form.services.filter((x) => x !== s) : [...form.services, s]
    );

  const hasTela = form.services.includes("Troca de tela");
  const hasBateria = form.services.includes("Troca de bateria");
  const hasSubirBateria = form.services.includes("Subir bateria");

  const canSave =
    form.clientName.trim() &&
    form.clientPhone.trim() &&
    form.model.trim() &&
    form.services.length > 0;

  const submit = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      await onSave({
        ...form,
        total: parseCurrency(form.total),
        costValue: isAdmin ? parseCurrency(form.costValue) : undefined,
        items: [],
        batteryPercent: (hasBateria || hasSubirBateria) ? form.batteryPercent || null : null,
        batteryMsg: hasBateria ? form.batteryMsg || null : null,
        telaMsg: hasTela ? form.telaMsg || null : null,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in"
      data-testid="new-order-modal"
    >
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden shadow-2xl flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-blue-600 to-blue-800 text-white">
          <div>
            <h3 className="font-heading text-lg font-bold">Nova Ordem de Serviço</h3>
            <p className="text-xs text-white/70 mt-0.5">Nº {form.orderNumber}</p>
          </div>
          <div className="flex items-center gap-2">
            {isAdmin && (
              <button
                onClick={() => setShowPriceLookup(true)}
                data-testid="new-order-price-lookup"
                title="Consultar tabela de preços"
                className="h-9 px-3 rounded-lg text-xs font-semibold bg-white/10 hover:bg-white/20 border border-white/20 text-white inline-flex items-center gap-1.5 transition-all"
              >
                <DollarSign className="w-3.5 h-3.5" /> Consultar preços
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
              data-testid="close-new-order"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto p-6 space-y-5">
          {/* Client */}
          <section>
            <h4 className="font-heading font-semibold text-slate-800 mb-2">Cliente</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2 relative">
                <label className="text-[11px] uppercase font-semibold text-slate-500">Nome do cliente *</label>
                <div className="relative">
                  <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-300" />
                  <input
                    data-testid="no-client-name"
                    value={form.clientName}
                    onChange={(e) => {
                      setField("clientName", e.target.value);
                      setClientQuery(e.target.value);
                    }}
                    placeholder="Digite ou busque um cliente"
                    className="w-full h-10 rounded-lg border border-slate-300 pl-8 pr-3 text-sm focus:border-blue-500 focus:outline-none"
                  />
                  {filteredClients.length > 0 && (
                    <ul className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
                      {filteredClients.map((c) => (
                        <li
                          key={c.id}
                          onClick={() => {
                            setForm((f) => ({
                              ...f,
                              clientId: c.id,
                              clientName: c.name,
                              clientPhone: c.phone,
                            }));
                            setClientQuery("");
                          }}
                          className="px-3 py-2 text-sm hover:bg-blue-50 cursor-pointer"
                        >
                          <div className="font-medium">{c.name}</div>
                          <div className="text-xs text-slate-400">{c.phone}</div>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold text-slate-500">Telefone *</label>
                <input
                  data-testid="no-client-phone"
                  value={form.clientPhone}
                  onChange={(e) => setField("clientPhone", e.target.value)}
                  placeholder="(11) 99999-9999"
                  className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={async () => {
                  if (!form.clientName.trim() || !form.clientPhone.trim()) {
                    return alert("Preencha o nome e o telefone antes de cadastrar.");
                  }
                  try {
                    await api.post("/clients", { name: form.clientName.trim(), phone: form.clientPhone.trim() });
                    setToast("Cliente cadastrado com sucesso!");
                    setTimeout(() => setToast(""), 2500);
                  } catch (e) {
                    const msg = e.response?.data?.detail || "Erro ao cadastrar cliente";
                    setToast(msg);
                    setTimeout(() => setToast(""), 3000);
                  }
                }}
                data-testid="no-register-client"
                className="h-9 px-3 rounded-md text-xs font-semibold bg-emerald-600 hover:bg-emerald-700 text-white inline-flex items-center gap-1.5"
                title="Cadastrar novo cliente sem sair da OS"
              >
                <UserPlus className="w-3.5 h-3.5" /> Cadastrar Cliente
              </button>
            </div>
            {toast && (
              <div className="mt-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="no-client-toast">
                {toast}
              </div>
            )}
          </section>

          {/* Device */}
          <section>
            <h4 className="font-heading font-semibold text-slate-800 mb-2">Aparelho</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div>
                <label className="text-[11px] uppercase font-semibold text-slate-500">Marca</label>
                <select
                  value={form.brand}
                  onChange={(e) => setField("brand", e.target.value)}
                  className="w-full h-10 rounded-lg border border-slate-300 px-2 text-sm bg-white focus:border-blue-500 focus:outline-none"
                >
                  {BRANDS.map((b) => (
                    <option key={b}>{b}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold text-slate-500">Modelo *</label>
                <input
                  list="model-list"
                  data-testid="no-model"
                  value={form.model}
                  onChange={(e) => setField("model", e.target.value)}
                  placeholder="ex.: iPhone 13"
                  className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
                />
                <datalist id="model-list">
                  {(MODELS[form.brand] || []).map((m) => (
                    <option key={m} value={m} />
                  ))}
                </datalist>
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold text-slate-500">Cor</label>
                <select
                  value={form.color}
                  onChange={(e) => setField("color", e.target.value)}
                  className="w-full h-10 rounded-lg border border-slate-300 px-2 text-sm bg-white focus:border-blue-500 focus:outline-none"
                >
                  <option value="">—</option>
                  {COLORS.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase font-semibold text-slate-500">IMEI ou OS</label>
                <input
                  value={form.imei}
                  onChange={(e) => setField("imei", e.target.value)}
                  data-testid="no-imei"
                  placeholder="Número do IMEI ou nº da OS externa"
                  className="w-full h-10 rounded-lg border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>
          </section>

          {/* Services */}
          <section>
            <h4 className="font-heading font-semibold text-slate-800 mb-2">Serviço *</h4>
            <div className="flex flex-wrap gap-2">
              {SERVICE_LIST.map((s) => {
                const active = form.services.includes(s);
                return (
                  <button
                    key={s}
                    onClick={() => toggleService(s)}
                    type="button"
                    data-testid={`service-${s}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                      active
                        ? "bg-blue-600 text-white border border-blue-600"
                        : "bg-white text-slate-600 border border-slate-200 hover:border-blue-300 hover:text-blue-700"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>

            {/* Custom service input */}
            <div className="mt-3 flex gap-2 items-center">
              <input
                value={customService}
                onChange={(e) => setCustomService(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && customService.trim()) {
                    e.preventDefault();
                    const v = customService.trim();
                    if (!form.services.includes(v)) {
                      setField("services", [...form.services, v]);
                    }
                    setCustomService("");
                  }
                }}
                placeholder="Outro serviço (digite e clique em adicionar)"
                data-testid="custom-service-input"
                className="flex-1 h-9 rounded-md border border-slate-300 px-3 text-sm focus:border-blue-500 focus:outline-none"
              />
              <button
                type="button"
                onClick={() => {
                  const v = customService.trim();
                  if (!v) return;
                  if (!form.services.includes(v)) {
                    setField("services", [...form.services, v]);
                  }
                  setCustomService("");
                }}
                disabled={!customService.trim()}
                data-testid="custom-service-add"
                className="h-9 px-3 rounded-md text-xs font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
              >
                + Adicionar
              </button>
            </div>

            {/* Selected services (including custom ones) with remove */}
            {form.services.filter((s) => !SERVICE_LIST.includes(s)).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {form.services.filter((s) => !SERVICE_LIST.includes(s)).map((s) => (
                  <span key={s} className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-[11px] font-semibold">
                    {s}
                    <button
                      type="button"
                      onClick={() => setField("services", form.services.filter((x) => x !== s))}
                      className="hover:text-purple-900"
                      data-testid={`remove-custom-${s}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Sub-options */}
            {hasTela && (
              <div className="mt-3 p-3 rounded-lg bg-blue-50/60 border border-blue-100 animate-slide-down">
                <div className="text-[11px] uppercase font-semibold text-blue-800 tracking-wider mb-1.5">
                  Troca de tela — mensagem &quot;peça original&quot;
                </div>
                <div className="flex gap-2">
                  {[
                    { v: "com", label: "Com mensagem" },
                    { v: "sem", label: "Sem mensagem" },
                  ].map((o) => (
                    <button
                      key={o.v}
                      type="button"
                      onClick={() => setField("telaMsg", o.v)}
                      data-testid={`telaMsg-${o.v}`}
                      className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                        form.telaMsg === o.v
                          ? "bg-blue-600 text-white"
                          : "bg-white text-blue-700 border border-blue-200 hover:border-blue-400"
                      }`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {(hasBateria || hasSubirBateria) && (
              <div className="mt-3 p-3 rounded-lg bg-emerald-50/60 border border-emerald-100 animate-slide-down space-y-3">
                {hasBateria && (
                  <div>
                    <div className="text-[11px] uppercase font-semibold text-emerald-800 tracking-wider mb-1.5">
                      Troca de bateria — mensagem
                    </div>
                    <div className="flex gap-2">
                      {[
                        { v: "com", label: "Com mensagem" },
                        { v: "sem", label: "Sem mensagem" },
                      ].map((o) => (
                        <button
                          key={o.v}
                          type="button"
                          onClick={() => setField("batteryMsg", o.v)}
                          data-testid={`batteryMsg-${o.v}`}
                          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                            form.batteryMsg === o.v
                              ? "bg-emerald-600 text-white"
                              : "bg-white text-emerald-700 border border-emerald-200 hover:border-emerald-400"
                          }`}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div>
                  <div className="text-[11px] uppercase font-semibold text-emerald-800 tracking-wider mb-1.5">
                    Percentual da bateria (%)
                  </div>
                  <div className="relative w-32">
                    <input
                      type="number"
                      min={0}
                      max={100}
                      value={form.batteryPercent}
                      onChange={(e) => setField("batteryPercent", e.target.value)}
                      placeholder="100"
                      data-testid="battery-percent"
                      className="w-full h-9 rounded-md border border-emerald-300 bg-white px-3 pr-8 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                    <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-emerald-700 text-sm font-bold">%</span>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Problem */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-500">Problema relatado</label>
              <textarea
                value={form.problem}
                onChange={(e) => setField("problem", e.target.value)}
                rows={2}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] uppercase font-semibold text-slate-500">Observação interna</label>
              <textarea
                value={form.observation}
                onChange={(e) => setField("observation", e.target.value)}
                rows={2}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
          </section>

          {/* Service value (optional) */}
          <section>
            <h4 className="font-heading font-semibold text-slate-800 mb-2">
              Valor do serviço <span className="text-slate-400 font-normal text-xs">(opcional)</span>
            </h4>
            <div className="flex items-end gap-3 flex-wrap">
              <div className="flex-1 max-w-xs">
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold">R$</span>
                  <input
                    inputMode="decimal"
                    value={form.total}
                    onChange={(e) => setField("total", e.target.value)}
                    placeholder="0,00"
                    data-testid="no-total-value"
                    className="w-full h-11 rounded-lg border border-slate-300 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none text-right tabular-nums font-semibold"
                  />
                </div>
                <p className="text-[11px] text-slate-400 mt-1">
                  Pode deixar em branco e definir depois no momento da entrega.
                </p>
              </div>
              {isAdmin && (
                <div className="flex-1 max-w-xs">
                  <label className="text-[10px] uppercase font-semibold text-slate-500 tracking-wider">
                    Custo da peça/serviço
                  </label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-semibold">R$</span>
                    <input
                      inputMode="decimal"
                      value={form.costValue}
                      onChange={(e) => setField("costValue", e.target.value)}
                      placeholder="0,00"
                      data-testid="no-cost-value"
                      className="w-full h-11 rounded-lg border border-slate-300 pl-10 pr-3 text-sm focus:border-blue-500 focus:outline-none text-right tabular-nums"
                    />
                  </div>
                  <p className="text-[11px] text-slate-400 mt-1">
                    Só admin vê. Pode editar depois em Saída ou no detalhe da OS.
                  </p>
                </div>
              )}
              <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-2.5 text-blue-800">
                <div className="text-[10px] uppercase font-bold tracking-wider">Total</div>
                <div className="font-heading text-xl font-extrabold tabular-nums">
                  R$ {fmtBRL(parseCurrency(form.total))}
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
          <button
            onClick={onClose}
            className="h-10 px-4 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-200 transition-colors"
          >
            Cancelar
          </button>
          <button
            onClick={submit}
            disabled={!canSave || saving}
            data-testid="save-new-order"
            className="h-10 px-5 rounded-lg text-sm font-semibold bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-2 transition-all hover:-translate-y-[1px] hover:shadow-lg disabled:opacity-50 disabled:hover:translate-y-0 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            {saving ? "Salvando..." : "Salvar OS"}
          </button>
        </div>
      </div>

      {showPriceLookup && (
        <PriceLookupModal
          initialQuery={form.model || ""}
          onClose={() => setShowPriceLookup(false)}
          onPick={(item) => {
            // Fill the "Total" with the sale price and optionally add the service
            const svc = (item.service || "").trim();
            const updates = { total: item.price };
            if (svc && !form.services.includes(svc)) {
              updates.services = [...form.services, svc];
            }
            setForm({ ...form, ...updates });
            setShowPriceLookup(false);
          }}
        />
      )}
    </div>
  );
}
